'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import type { InterfaceToken } from '@morpho-org/dapp-core';
import type { DialogOptions } from '@morpho-org/ui-v6/client';
import type { ComponentProps, JSX } from 'react';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import type { ActionButtons } from '@/components/common/ActionButtons';
import type { useVaultV2WithdrawLogic } from '@/components/VaultPage/VaultV2/hooks/useVaultV2WithdrawLogic';
import type { InKindRedemptionEntrySource } from '@/lib/analytics/client/events';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { EXIT_BUNDLE_NO_LIQUIDITY_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { useWeb3Context } from '@/contexts/Web3Context';
import useBundleCanTransferOnchain from '@/hooks/operation/useBundleCanTransferOnchain';
import { useTrackInKindRedemptionEntryViewed } from '@/lib/analytics/client/hooks/useTrackInKindRedemptionEntryViewed';
import type { InKindRedemptionAction } from './inKindRedemptionEntryAction';
import InsufficientLiquidityCallout from '../InsufficientLiquidityCallout';
import { buildInKindRedemptionEntryAction } from './inKindRedemptionEntryAction';
interface UseInKindRedemptionEntryParams {
  source: InKindRedemptionEntrySource;
  chainId: ChainId | undefined;
  vaultAddress: Address;
  vaultAsset: InterfaceToken | null | undefined;
  logic: ReturnType<typeof useVaultV2WithdrawLogic>;
  /** Opens the plain-withdraw review, used at every tier below the in-kind one. */
  onReview: () => void;
  dialogOptions?: DialogOptions;
  onTxSuccess?: () => void;
}
type ActionButtonProps = Pick<ComponentProps<typeof ActionButtons>, 'handleFinalize' | 'isLoading' | 'finalizeLabel' | 'tooltipContent' | 'isFinalizeDisabled'>;

/**
 * The withdraw-liquidity surface wiring shared by the vault-page withdraw form and the
 * position-manager withdraw dialog: the entry-viewed impression, the tier 1/3 liquidity
 * callout, and the resolved primary-button props — one button at every tier, only the
 * destination changes, so at tier 3 it opens in-kind redemption instead of the review.
 * Each surface only contributes its `source`, `chainId`, review handler and dialog options.
 */
export function useInKindRedemptionEntry(t0) {
  const $ = _c(39);
  const {
    source,
    chainId,
    vaultAddress,
    vaultAsset,
    logic,
    onReview,
    dialogOptions,
    onTxSuccess
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    account
  } = useWeb3Context();
  const {
    canTransfer: exitBundleCanTransfer
  } = useBundleCanTransferOnchain("bundles.vaultExitBundlesV1", vaultAsset?.address, chainId);
  const {
    liquidityCallout,
    isInKindRedemptionEligible,
    applyServiceableMax,
    showInKindRedemptionEntry,
    inputAmount,
    userVaultAssets,
    isActionLoading,
    disabledLabel,
    tooltipMessage
  } = logic;
  let t1;
  if ($[0] !== account || $[1] !== chainId || $[2] !== isActionLoading || $[3] !== showInKindRedemptionEntry) {
    t1 = showInKindRedemptionEntry && account.isConnected && !account.isWrongChain && !isActionLoading && isDefined(chainId);
    $[0] = account;
    $[1] = chainId;
    $[2] = isActionLoading;
    $[3] = showInKindRedemptionEntry;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== chainId || $[6] !== source || $[7] !== t1 || $[8] !== vaultAddress) {
    t2 = {
      enabled: t1,
      source,
      chainId,
      vaultAddress
    };
    $[5] = chainId;
    $[6] = source;
    $[7] = t1;
    $[8] = vaultAddress;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  useTrackInKindRedemptionEntryViewed(t2);
  let t3;
  if ($[10] !== chainId || $[11] !== dialogOptions || $[12] !== inputAmount || $[13] !== onTxSuccess || $[14] !== showInKindRedemptionEntry || $[15] !== source || $[16] !== toggleDialog || $[17] !== userVaultAssets || $[18] !== vaultAddress || $[19] !== vaultAsset) {
    t3 = showInKindRedemptionEntry ? buildInKindRedemptionEntryAction({
      source,
      chainId,
      vaultAddress,
      requestedExitAssets: inputAmount,
      userPosition: userVaultAssets,
      vaultAsset,
      toggleDialog,
      dialogOptions,
      onTxSuccess
    }) : undefined;
    $[10] = chainId;
    $[11] = dialogOptions;
    $[12] = inputAmount;
    $[13] = onTxSuccess;
    $[14] = showInKindRedemptionEntry;
    $[15] = source;
    $[16] = toggleDialog;
    $[17] = userVaultAssets;
    $[18] = vaultAddress;
    $[19] = vaultAsset;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  const inKindRedemptionAction = t3;
  let t4;
  if ($[21] !== applyServiceableMax || $[22] !== exitBundleCanTransfer || $[23] !== isInKindRedemptionEligible || $[24] !== liquidityCallout || $[25] !== vaultAsset) {
    t4 = liquidityCallout && liquidityCallout.variant === "insufficient" && liquidityCallout.assets === ZERO && !exitBundleCanTransfer ? <BundleAuthorizationCallout variant="destructive" message={EXIT_BUNDLE_NO_LIQUIDITY_MESSAGE} /> : liquidityCallout ? <InsufficientLiquidityCallout variant={liquidityCallout.variant} assets={liquidityCallout.assets} walletAssets={liquidityCallout.walletAssets} feeAssets={liquidityCallout.feeAssets} vaultAsset={vaultAsset} isInKindRedemptionEligible={isInKindRedemptionEligible} onAmountClick={applyServiceableMax} /> : null;
    $[21] = applyServiceableMax;
    $[22] = exitBundleCanTransfer;
    $[23] = isInKindRedemptionEligible;
    $[24] = liquidityCallout;
    $[25] = vaultAsset;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  const t5 = inKindRedemptionAction?.onClick ?? onReview;
  const t6 = inKindRedemptionAction?.label ?? disabledLabel ?? "Review Withdraw";
  let t7;
  if ($[27] !== inKindRedemptionAction?.tooltipContent || $[28] !== tooltipMessage) {
    t7 = inKindRedemptionAction?.tooltipContent ?? (tooltipMessage ? <span className="text-3xs" style={tooltipMessage.textStyle}>{tooltipMessage.message}</span> : undefined);
    $[27] = inKindRedemptionAction?.tooltipContent;
    $[28] = tooltipMessage;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const t8 = !inKindRedemptionAction && !!disabledLabel;
  let t9;
  if ($[30] !== isActionLoading || $[31] !== t5 || $[32] !== t6 || $[33] !== t7 || $[34] !== t8) {
    t9 = {
      handleFinalize: t5,
      isLoading: isActionLoading,
      finalizeLabel: t6,
      tooltipContent: t7,
      isFinalizeDisabled: t8
    };
    $[30] = isActionLoading;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== t4 || $[37] !== t9) {
    t10 = {
      liquidityCallout: t4,
      actionButtonProps: t9
    };
    $[36] = t4;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
}